type WebSecurityEnvironment = Record<string, string | undefined>;

const LOCAL_DEV_API_ORIGIN = 'http://localhost:3001';
const LOCAL_DEV_STORAGE_ORIGIN = 'http://localhost:9000';
const LOCAL_DEV_STORAGE_FALLBACK_ORIGINS = [
  'http://localhost:9000',
  'http://127.0.0.1:9000',
] as const;

function originOf(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : null;
  } catch {
    return null;
  }
}

function configuredOrigins(env: WebSecurityEnvironment, key: string): string[] {
  return (env[key] ?? '')
    .split(',')
    .map((value) => originOf(value))
    .filter((value): value is string => Boolean(value));
}

function uniqueSources(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

function websocketOrigin(origin: string | null): string | null {
  if (!origin) return null;
  return origin.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
}

export function createCspNonce(): string {
  return crypto.randomUUID().replaceAll('-', '');
}

/** Ensures CSP directives include API/storage origins in local dev when monorepo root env is absent from middleware. */
export function resolveWebSecurityEnvironment(
  env: WebSecurityEnvironment = typeof process !== 'undefined' ? process.env : {},
): WebSecurityEnvironment {
  const production = env.NODE_ENV === 'production';
  const apiUrl = env.NEXT_PUBLIC_API_URL ?? env.API_PUBLIC_URL;
  const storageUrl = env.NEXT_PUBLIC_CDN_URL ?? env.S3_PUBLIC_URL;
  const resolvedApiUrl = apiUrl ?? (production ? undefined : LOCAL_DEV_API_ORIGIN);
  const resolvedStorageUrl = storageUrl ?? (production ? undefined : LOCAL_DEV_STORAGE_ORIGIN);

  return {
    ...env,
    NEXT_PUBLIC_API_URL: resolvedApiUrl,
    API_PUBLIC_URL: env.API_PUBLIC_URL ?? resolvedApiUrl,
    NEXT_PUBLIC_CDN_URL: env.NEXT_PUBLIC_CDN_URL ?? resolvedStorageUrl,
    S3_PUBLIC_URL: env.S3_PUBLIC_URL ?? resolvedStorageUrl,
  };
}

export function buildContentSecurityPolicy(
  nonce: string,
  env: WebSecurityEnvironment,
): string {
  const resolvedEnv = resolveWebSecurityEnvironment(env);
  const production = resolvedEnv.NODE_ENV === 'production';
  const apiOrigin = originOf(resolvedEnv.NEXT_PUBLIC_API_URL ?? resolvedEnv.API_PUBLIC_URL);
  const storageOrigin = originOf(resolvedEnv.NEXT_PUBLIC_CDN_URL ?? resolvedEnv.S3_PUBLIC_URL);
  const sentryOrigin = originOf(resolvedEnv.NEXT_PUBLIC_SENTRY_DSN);
  const presignOrigins = uniqueSources([
    originOf(resolvedEnv.S3_PRESIGN_ENDPOINT),
    originOf(resolvedEnv.S3_QUARANTINE_PRESIGN_ENDPOINT),
    originOf(resolvedEnv.S3_EXPORT_PRESIGN_ENDPOINT),
  ]);
  const connectSources = uniqueSources([
    "'self'",
    apiOrigin,
    websocketOrigin(apiOrigin),
    storageOrigin,
    ...presignOrigins,
    sentryOrigin,
    ...(production ? [] : LOCAL_DEV_STORAGE_FALLBACK_ORIGINS),
    ...configuredOrigins(resolvedEnv, 'CSP_CONNECT_ORIGINS'),
  ]);
  const imageSources = uniqueSources([
    "'self'",
    'data:',
    'blob:',
    storageOrigin,
    ...(production ? [] : LOCAL_DEV_STORAGE_FALLBACK_ORIGINS),
    ...configuredOrigins(resolvedEnv, 'CSP_IMAGE_ORIGINS'),
  ]);
  const mediaSources = uniqueSources([
    "'self'",
    'blob:',
    storageOrigin,
    ...(production ? [] : LOCAL_DEV_STORAGE_FALLBACK_ORIGINS),
    ...configuredOrigins(resolvedEnv, 'CSP_MEDIA_ORIGINS'),
  ]);
  const frameSources = uniqueSources([
    "'self'",
    'https://js.stripe.com',
    'https://hooks.stripe.com',
    ...configuredOrigins(resolvedEnv, 'CSP_FRAME_ORIGINS'),
  ]);
  const fontSources = uniqueSources([
    "'self'",
    'data:',
    ...configuredOrigins(resolvedEnv, 'CSP_FONT_ORIGINS'),
  ]);
  const scriptSources = uniqueSources([
    "'self'",
    `'nonce-${nonce}'`,
    "'strict-dynamic'",
    'https://js.stripe.com',
    ...(production ? [] : ["'unsafe-eval'"]),
    ...configuredOrigins(resolvedEnv, 'CSP_SCRIPT_ORIGINS'),
  ]);

  const directives = [
    "default-src 'self'",
    `script-src ${scriptSources.join(' ')}`,
    `style-src 'self' 'nonce-${nonce}'`,
    // Trusted UI bundles may inject <style> nodes at runtime; script-src stays strict.
    `style-src-elem 'self' 'nonce-${nonce}' 'unsafe-inline'`,
    // Inline style attributes are emitted by React and UI libraries; style elements remain nonced.
    "style-src-attr 'unsafe-inline'",
    `connect-src ${connectSources.join(' ')}`,
    `img-src ${imageSources.join(' ')}`,
    `media-src ${mediaSources.join(' ')}`,
    `font-src ${fontSources.join(' ')}`,
    `frame-src ${frameSources.join(' ')}`,
    "frame-ancestors 'none'",
    "object-src 'none'",
    "base-uri 'self'",
    "form-action 'self'",
    "manifest-src 'self'",
    "worker-src 'self' blob:",
    ...(production ? ['upgrade-insecure-requests'] : []),
  ];
  return directives.join('; ');
}

export function applyWebSecurityHeaders(
  headers: Headers,
  nonce: string,
  env: WebSecurityEnvironment,
): void {
  const resolvedEnv = resolveWebSecurityEnvironment(env);
  headers.set('Content-Security-Policy', buildContentSecurityPolicy(nonce, resolvedEnv));
  headers.set('X-Content-Type-Options', 'nosniff');
  headers.set('X-Frame-Options', 'DENY');
  headers.set('Referrer-Policy', 'strict-origin-when-cross-origin');
  headers.set('Permissions-Policy', 'camera=(), microphone=(), geolocation=()');
  headers.set('Cross-Origin-Opener-Policy', 'same-origin');
  if (resolvedEnv.NODE_ENV === 'production') {
    headers.set('Strict-Transport-Security', 'max-age=31536000; includeSubDomains');
  }
  const secure = resolvedEnv.NODE_ENV === 'production' ? '; Secure' : '';
  headers.append(
    'Set-Cookie',
    `loamify_csp_nonce=${encodeURIComponent(nonce)}; Path=/; SameSite=Strict; Max-Age=300${secure}`,
  );
}
